import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getPromptLibrary,
  getFavorites,
  addFavorite as addFavoriteApi,
  addCustomFavorite as addCustomFavoriteApi,
  removeFavorite as removeFavoriteApi,
} from "../../api/promptLibrary";
import { showSnackbar } from "./snackbar";

const errorFrom = (error, fallback) => ({
  status: error?.response?.status || null,
  message:
    error?.response?.data?.detail ||
    error?.response?.statusText ||
    error?.message ||
    fallback,
});

const tempIdFor = (requestId) => `temp-${requestId}`;

export const fetchPromptLibrary = createAsyncThunk(
  "promptLibrary/fetch",
  async (audience, { rejectWithValue }) => {
    try {
      return await getPromptLibrary(audience);
    } catch (error) {
      return rejectWithValue(errorFrom(error, "Failed to load prompt library"));
    }
  },
);

export const fetchFavorites = createAsyncThunk(
  "promptLibrary/fetchFavorites",
  async (_, { rejectWithValue }) => {
    try {
      return await getFavorites();
    } catch (error) {
      return rejectWithValue(errorFrom(error, "Failed to load favorites"));
    }
  },
);

// Star / unstar a catalog prompt. The optimistic mutation happens in the
// pending reducer; the API call only confirms it (and swaps the temp
// favorite_id for the real one on add) or triggers a rollback on reject.
export const toggleLibraryFavorite = createAsyncThunk(
  "promptLibrary/toggleLibraryFavorite",
  async (promptRow, { getState, requestId, dispatch, rejectWithValue }) => {
    try {
      if (!promptRow.is_favorite) {
        const res = await addFavoriteApi(promptRow.prompt_id);
        return {
          op: "added",
          prompt_id: promptRow.prompt_id,
          favorite_id: res?.favorite_id,
        };
      }
      // Removing: the real favorite_id was stashed by the pending reducer.
      const favId = getState().promptLibrary.pendingOps[requestId]?.favId;
      if (!favId || String(favId).startsWith("temp-")) {
        dispatch(
          showSnackbar({
            message: "Please wait for the favorite to finish saving.",
            severity: "warning",
          }),
        );
        return rejectWithValue({ status: null, message: "Favorite not ready" });
      }
      await removeFavoriteApi(favId);
      return {
        op: "removed",
        prompt_id: promptRow.prompt_id,
        favorite_id: favId,
      };
    } catch (error) {
      const e = errorFrom(error, "Could not update favorite");
      dispatch(showSnackbar({ message: e.message, severity: "error" }));
      return rejectWithValue(e);
    }
  },
);

// Freeform favorite (chat prompts / typed text) — no prompt_id.
export const addCustomFavoriteThunk = createAsyncThunk(
  "promptLibrary/addCustomFavorite",
  async (text, { dispatch, rejectWithValue }) => {
    try {
      return await addCustomFavoriteApi(text);
    } catch (error) {
      const e = errorFrom(error, "Could not add favorite");
      dispatch(showSnackbar({ message: e.message, severity: "error" }));
      return rejectWithValue(e);
    }
  },
);

// Remove any favorite by its favorite_id (Favorites tab + chat star).
export const removeFavoriteById = createAsyncThunk(
  "promptLibrary/removeFavoriteById",
  async (favoriteId, { dispatch, rejectWithValue }) => {
    try {
      await removeFavoriteApi(favoriteId);
      return { favorite_id: favoriteId };
    } catch (error) {
      const e = errorFrom(error, "Could not remove favorite");
      dispatch(showSnackbar({ message: e.message, severity: "error" }));
      return rejectWithValue(e);
    }
  },
);

// Flip is_favorite on the matching catalog prompt wherever it appears (Immer draft).
const setFavoriteFlag = (categories, promptId, value) => {
  Object.values(categories || {}).forEach((rows) => {
    (rows || []).forEach((row) => {
      if (row.prompt_id === promptId) row.is_favorite = value;
    });
  });
};

const promptLibrarySlice = createSlice({
  name: "promptLibrary",
  initialState: {
    categories: {},
    favorites: [],
    promptIdToFavoriteId: {},
    pendingOps: {}, // requestId -> rollback snapshot for optimistic removes
    libraryStatus: "idle", // idle | loading | succeeded | failed
    favoritesStatus: "idle", // idle | loading | succeeded | failed
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // --- library fetch ---
      .addCase(fetchPromptLibrary.pending, (state) => {
        state.libraryStatus = "loading";
        state.error = null;
      })
      .addCase(fetchPromptLibrary.fulfilled, (state, action) => {
        state.libraryStatus = "succeeded";
        state.categories = action.payload?.categories || {};
      })
      .addCase(fetchPromptLibrary.rejected, (state, action) => {
        state.libraryStatus = "failed";
        state.error = action.payload || {
          status: null,
          message: "Failed to load prompt library",
        };
        state.categories = {};
      })
      // --- favorites fetch (also builds the prompt_id -> favorite_id map) ---
      .addCase(fetchFavorites.pending, (state) => {
        state.favoritesStatus = "loading";
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.favoritesStatus = "succeeded";
        const favs = action.payload?.favorites || [];
        state.favorites = favs;
        state.promptIdToFavoriteId = {};
        favs.forEach((f) => {
          if (f.prompt_id != null) {
            state.promptIdToFavoriteId[f.prompt_id] = f.favorite_id;
          }
        });
      })
      .addCase(fetchFavorites.rejected, (state) => {
        state.favoritesStatus = "failed";
      })
      // --- toggle catalog favorite (optimistic) ---
      .addCase(toggleLibraryFavorite.pending, (state, action) => {
        const row = action.meta.arg;
        const { requestId } = action.meta;
        const pid = row.prompt_id;
        if (!row.is_favorite) {
          const tempId = tempIdFor(requestId);
          setFavoriteFlag(state.categories, pid, true);
          state.promptIdToFavoriteId[pid] = tempId;
          state.favorites.unshift({
            ...row,
            is_favorite: true,
            favorite_id: tempId,
          });
        } else {
          const favId = state.promptIdToFavoriteId[pid];
          const removed = state.favorites.find((f) => f.favorite_id === favId);
          state.pendingOps[requestId] = {
            pid,
            favId,
            removedRow: removed ? { ...removed } : null,
          };
          setFavoriteFlag(state.categories, pid, false);
          delete state.promptIdToFavoriteId[pid];
          state.favorites = state.favorites.filter(
            (f) => f.favorite_id !== favId,
          );
        }
      })
      .addCase(toggleLibraryFavorite.fulfilled, (state, action) => {
        const { op, prompt_id, favorite_id } = action.payload;
        const { requestId } = action.meta;
        if (op === "added") {
          const tempId = tempIdFor(requestId);
          state.promptIdToFavoriteId[prompt_id] = favorite_id;
          const entry = state.favorites.find((f) => f.favorite_id === tempId);
          if (entry) entry.favorite_id = favorite_id;
        }
        delete state.pendingOps[requestId];
      })
      .addCase(toggleLibraryFavorite.rejected, (state, action) => {
        const row = action.meta.arg;
        const { requestId } = action.meta;
        const pid = row.prompt_id;
        if (!row.is_favorite) {
          // revert optimistic add
          const tempId = tempIdFor(requestId);
          setFavoriteFlag(state.categories, pid, false);
          if (state.promptIdToFavoriteId[pid] === tempId) {
            delete state.promptIdToFavoriteId[pid];
          }
          state.favorites = state.favorites.filter(
            (f) => f.favorite_id !== tempId,
          );
        } else {
          // revert optimistic remove
          const op = state.pendingOps[requestId];
          if (op) {
            setFavoriteFlag(state.categories, pid, true);
            state.promptIdToFavoriteId[pid] = op.favId;
            if (
              op.removedRow &&
              !state.favorites.some((f) => f.favorite_id === op.favId)
            ) {
              state.favorites.unshift(op.removedRow);
            }
          }
        }
        delete state.pendingOps[requestId];
      })
      // --- add custom favorite (optimistic temp entry) ---
      .addCase(addCustomFavoriteThunk.pending, (state, action) => {
        state.favorites.unshift({
          favorite_id: tempIdFor(action.meta.requestId),
          prompt_id: null,
          audience: null,
          category: "My Prompts",
          prompt: action.meta.arg,
          source_line: null,
          is_active: true,
          is_favorite: true,
          is_custom: true,
          favorited_at: null,
          created_at: null,
          updated_at: null,
        });
      })
      .addCase(addCustomFavoriteThunk.fulfilled, (state, action) => {
        const tempId = tempIdFor(action.meta.requestId);
        const entry = state.favorites.find((f) => f.favorite_id === tempId);
        if (entry) entry.favorite_id = action.payload?.favorite_id;
      })
      .addCase(addCustomFavoriteThunk.rejected, (state, action) => {
        const tempId = tempIdFor(action.meta.requestId);
        state.favorites = state.favorites.filter(
          (f) => f.favorite_id !== tempId,
        );
      })
      // --- remove by favorite_id (optimistic) ---
      .addCase(removeFavoriteById.pending, (state, action) => {
        const favId = action.meta.arg;
        const { requestId } = action.meta;
        const entry = state.favorites.find((f) => f.favorite_id === favId);
        const pid = entry?.prompt_id ?? null;
        state.pendingOps[requestId] = {
          favId,
          pid,
          removedRow: entry ? { ...entry } : null,
        };
        if (pid != null) {
          setFavoriteFlag(state.categories, pid, false);
          delete state.promptIdToFavoriteId[pid];
        }
        state.favorites = state.favorites.filter(
          (f) => f.favorite_id !== favId,
        );
      })
      .addCase(removeFavoriteById.fulfilled, (state, action) => {
        delete state.pendingOps[action.meta.requestId];
      })
      .addCase(removeFavoriteById.rejected, (state, action) => {
        const op = state.pendingOps[action.meta.requestId];
        if (op) {
          if (
            op.removedRow &&
            !state.favorites.some((f) => f.favorite_id === op.favId)
          ) {
            state.favorites.unshift(op.removedRow);
          }
          if (op.pid != null) {
            setFavoriteFlag(state.categories, op.pid, true);
            state.promptIdToFavoriteId[op.pid] = op.favId;
          }
        }
        delete state.pendingOps[action.meta.requestId];
      });
  },
});

export default promptLibrarySlice.reducer;
